import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './PartnersSection.css';

const PARTNERS_PER_PAGE = 8;
const PARTNER_CATEGORIES = ['All', 'Government', 'International', 'Media', 'Academic Institution'];

// Keep the original partners data here for now
export const partners = [
    // Moroccan Government Institutions
    {
      name: 'The Moroccan Ministry of Youth, Culture and Communication',
      logo: '../images/partners/ministere.png',
      description: 'The Moroccan ministry responsible for youth, culture and communication',
      category: 'Government'
    },
    {
      name: 'Ministry Delegate to the Head of Government in charge of Relations with Parliament',
      logo: '../images/partners/relations_parliament.png',
      description: 'Moroccan government ministry responsible for relations with Parliament',
      category: 'Government'
    },
    {
      name: 'Moroccan Interministerial Delegation for Human Rights',
      logo: '../images/partners/interministerial_human_rights.png',
      description: 'Delegation promoting coordination among ministries for human rights protection',
      category: 'Government'
    },
    {
      name: 'Moroccan Council of Human Rights',
      logo: '../images/partners/cndh.png',
      description: 'Institution responsible for protecting and promoting human rights in Morocco',
      category: 'Government'
    },
    {
      name: 'Provincial Committee of the National Initiative for Human Development (INDH)',
      logo: '../images/partners/indh.png',
      description: 'Provincial committee working on human development initiatives',
      category: 'Government'
    },
    {
      name: 'ANAPEC',
      logo: '../images/partners/anapec.png',
      description: 'Moroccan national agency for employment and skills development',
      category: 'Government'
    },
    {
      name: 'Entraide Nationale Maroc',
      logo: '../images/partners/entraide.png',
      description: 'National mutual aid organization in Morocco',
      category: 'Government'
    },
    
    // Local & Regional Authorities
    {
      name: 'Region of Tangier-Tetouan-Al Hoceima',
      logo: '../images/partners/region-tangier.png',
      description: 'Regional government authority in northern Morocco',
      category: 'Government'
    },
    {
      name: 'Tangier-Assilah Prefecture',
      logo: '../images/partners/tangier-assilah-prefec.png',
      description: 'Local administrative authority managing the Tangier-Assilah area',
      category: 'Government'
    },
    {
      name: 'Assilah Council',
      logo: '../images/partners/assilah-council.png',
      description: 'Local municipal government of Assilah, Morocco',
      category: 'Government'
    },
    {
      name: 'The Assilah Forum Foundation',
      logo: '../images/partners/assilah-forum.png',
      description: "Assilah Forum Foundation fosters global cultural exchange",
      category: 'Government'
    },
    
    // Major International Organizations
    {
      name: 'UNESCO',
      logo: '../images/partners/unesco.png',
      description: 'United Nations Educational, Scientific and Cultural Organization',
      category: 'International'
    },
    {
      name: 'UNDP',
      logo: '../images/partners/undp.png',
      description: 'United Nations Development Programme',
      category: 'International'
    },
    {
      name: 'United Nations Population Fund (UNFPA)',
      logo: '../images/partners/unfpa.png',
      description: 'UN agency aiming to improve reproductive and maternal health worldwide',
      category: 'International'
    },
    {
      name: 'Council of Europe',
      logo: '../images/partners/coe.png',
      description: 'The Council of Europe promotes human rights, democracy, and the rule of law',
      category: 'International'
    },
    {
      name: 'League of Arab States (LAS)',
      logo: '../images/partners/arab-league.png',
      description: 'Regional organization promoting political, economic, and cultural cooperation among Arab countries',
      category: 'International'
    },
    {
      name: 'Union for the Mediterranean',
      logo: '../images/partners/uftm.png',
      description: 'Intergovernmental organization promoting regional cooperation in the Mediterranean',
      category: 'International'
    },
    {
      name: 'ICESCO',
      logo: '../images/partners/icesco.png',
      description: 'Islamic World Educational, Scientific and Cultural Organization',
      category: 'International'
    },
    
    // Academic Institutions
    {
      name: 'Mohammed V University Rabat',
      logo: '../images/partners/uni-med-5.png',
      description: 'Major public university located in Rabat, Morocco',
      category: 'Academic Institution'
    },
    
    // Media Partners
    {
      name: '2M TV',
      logo: '../images/partners/2m.png',
      description: 'Moroccan public television channel',
      category: 'Media'
    },
    {
      name: 'Medi 1 TV',
      logo: '../images/partners/medi1.png',
      description: 'Moroccan television channel focusing on news and cultural programs',
      category: 'Media'
    },
    {
      name: 'MAP - Moroccan Press Agency',
      logo: '../images/partners/map.png',
      description: 'Official news agency of Morocco',
      category: 'Media'
    },
    {
      name: 'National Society of Radio and Television of Morocco',
      logo: '../images/partners/snrt.png',
      description: 'Moroccan national broadcasting corporation',
      category: 'Media'
    },
    
    // International & Cultural Organizations
    {
      name: 'Erasmus+',
      logo: '../images/partners/erasmus.png',
      description: 'European Union program supporting education, training, youth, and sports',
      category: 'International'
    },
    {
      name: 'Konrad Adenauer Stiftung',
      logo: '../images/partners/konrad.png',
      description: 'German political foundation promoting democracy and rule of law',
      category: 'International'
    },
    {
      name: 'Bibliotheca Alexandrina',
      logo: '../images/partners/alexandria-bibliotheca.png',
      description: 'Major library and cultural center located in Alexandria, Egypt',
      category: 'International'
    },
    {
      name: 'British Council',
      logo: '../images/partners/british_council.png',
      description: 'International organization for cultural relations and educational opportunities',
      category: 'International'
    },
    {
      name: 'Tunivisions Foundation',
      logo: '../images/partners/tunivision.png',
      description: 'Tunisian organization promoting youth empowerment',
      category: 'International'
    },
    {
      name: 'US Embassy in Morocco',
      logo: '../images/partners/us-embassy.png',
      description: 'Official diplomatic representation of the United States in Morocco',
      category: 'Government'
    }
];

function PartnersSection() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Memoize filtered partners
  const filteredPartners = useMemo(() => {
    const filtered = selectedCategory === 'All' 
      ? partners 
      : partners.filter(partner => partner.category === selectedCategory);
    return filtered;
  }, [selectedCategory]);

  // Memoize current page partners
  const currentPartners = useMemo(() => {
    const indexOfLastPartner = currentPage * PARTNERS_PER_PAGE;
    const indexOfFirstPartner = indexOfLastPartner - PARTNERS_PER_PAGE;
    const current = filteredPartners.slice(indexOfFirstPartner, indexOfLastPartner);
    return current;
  }, [currentPage, filteredPartners]);

  const totalPages = Math.ceil(filteredPartners.length / PARTNERS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  const handlePartnershipClick = () => {
    navigate('/partnership-form');
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className='partners-container'>
      <div className='partners-content'>
        <h2 className='partners-heading'>Our Partners</h2>
        <p className='partners-subtitle'>
          Working together to create positive change in the Mediterranean region
        </p>
        
        <div className='partners-filter'>
          {PARTNER_CATEGORIES.map(category => (
            <button
              key={category}
              className={`filter-button ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className='partners-grid'>
          {currentPartners.map((partner, index) => (
            <div key={index} className='partner-card'>
              <img src={partner.logo} alt={partner.name} className='partner-logo' />
              <h3>{partner.name}</h3>
              <p>{partner.description}</p>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className='pagination'>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                className={`page-button ${currentPage === index + 1 ? 'active' : ''}`}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}

        <div className='partnership-cta'>
          <h3>Interested in Partnering with Us?</h3>
          <p>Join us in our mission to empower Mediterranean youth</p>
          <button 
            className='partnership-button'
            onClick={handlePartnershipClick}
          >
            Become a Partner
          </button>
        </div>
      </div>
    </div>
  );
}

export default PartnersSection; 